/**
 * The stage vocabulary (SCRUM-281) — the client's half of the `stage` SSE
 * contract. Mirrors `garden/chat/sse.py` `STAGES` 1:1 (a test reads that file,
 * so the two cannot drift silently). The server refuses to emit anything
 * outside it, so an unknown key here means a NEWER server than this bundle:
 * it renders as the generic "working" label — still true — rather than as a
 * raw key or a stale stage.
 */
import type { StageEvent } from "@/features/chat/useChatStream";

export const KNOWN_STAGES = [
  "understanding",
  "searching_products",
  "checking_sellers",
  "reading_document",
  "reading_quote",
  "searching_knowledge",
  "composing",
  "suggesting_next",
] as const;

export type KnownStage = (typeof KNOWN_STAGES)[number];

export function isKnownStage(key: string): key is KnownStage {
  return (KNOWN_STAGES as readonly string[]).includes(key);
}

/**
 * The `chat` i18n key (and interpolation values) for a stage's label. A
 * multi-file read names the file it is on ("2 of 3"); a single file does not
 * — "1 of 1" is noise.
 */
export function stageLabelKey(stage: StageEvent): {
  key: string;
  values?: { current: number; total: number };
} {
  if (
    stage.key === "reading_document" &&
    stage.current !== undefined &&
    stage.total !== undefined &&
    stage.total >= 2
  ) {
    return {
      key: "stage.reading_document_count",
      values: { current: stage.current, total: stage.total },
    };
  }
  return { key: isKnownStage(stage.key) ? `stage.${stage.key}` : "stage.working" };
}
